import { Button } from "@heroui/react";
import React, { type FC, memo, useCallback, useState } from "react";

import { FileUploader } from "@dms/modules/DocumentUploader/FileUploader";
import { FileUploaderProvider } from "@dms/modules/DocumentUploader/context/FileUploaderContext";
import { DmsType } from "@domain/models/dms";
import { useTranslation } from "@domain/intl/useTranslation";

import type { AgentPanelProps } from "./agentPanels";

const documentType = [DmsType.new_documents];
const noop = (): void => undefined;

const UploadAgentPanelImpl: FC<AgentPanelProps> = ({ panel, onDone, onBusy }) => {
    const { t } = useTranslation();
    const onUploaded = panel.type === "upload" ? panel.onUploaded : undefined;

    const handleUploaded = useCallback(
        (ids: string[]) => {
            onUploaded?.(ids);
            onDone(ids.length > 0 ? "done" : "skipped");
        },
        [onUploaded, onDone],
    );
    const handleSkip = useCallback(() => onDone("skipped"), [onDone]);
    // Skipping mid-upload would unmount the uploader and cut the write off.
    const [uploading, setUploading] = useState(false);
    const handleUploading = useCallback(
        (busy: boolean) => {
            setUploading(busy);
            onBusy(busy);
        },
        [onBusy],
    );

    return (
        <div className="flex flex-col gap-2">
            <FileUploaderProvider
                documentType={documentType}
                onAutoCloseAfterUpload={handleUploaded}
                onUploadingChange={handleUploading}
                onClose={noop}
            >
                <FileUploader isDropzoneExpanded={false} onCollapseDropzone={noop} />
            </FileUploaderProvider>
            <Button variant="outline" isDisabled={uploading} onPress={handleSkip}>
                {t("app.agent.start.skipUpload")}
            </Button>
        </div>
    );
};

export const UploadAgentPanel = memo(UploadAgentPanelImpl);
